import debounce from "debounce";
import {proxy, ref} from "valtio";

import {
    LocationSearchWithFallback,
    type LocationSearchWithFallbackInstance,
} from "@/features/locations/location-search/location-search-with-fallback.model";
import {AdvancedFilters, type AdvancedFiltersInstance} from "@/features/rental/advanced-filters";
import {DateFlexibility, type DateFlexibilityInstance} from "@/features/rental/date-flexibility";
import {GuestsStepper, type GuestsStepperInstance} from "@/features/rental/guests";

import {AdvanceFilter} from "@/entities/rental/types";

import {IQueryParamProvider} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {mergeQueryParams} from "@/shared/lib";
import {IValtioModel} from "@/shared/models/base-model.interface";
import {Checkbox} from "@/shared/models/form/fields/checkbox";

interface PropertyFiltersEvents {
    reset: () => void;
    change: () => void;
    apply: () => void;
    applyLocation: () => void;
}

const EVENTS = Symbol("EVENTS");

interface PropertyFilterConfig {
    filter: AdvanceFilter;
}
class PropertyFiltersClass implements IValtioModel, IQueryParamProvider {
    locationSearch: LocationSearchWithFallbackInstance;
    guests: GuestsStepperInstance;
    dateFlexibility: DateFlexibilityInstance;
    advancedFilters: AdvancedFiltersInstance;
    readonly events: EventEmitter<PropertyFiltersEvents>;
    private readonly debouncedEmitChange: (() => void) & {clear: () => void};
    private lastAppliedFilters: string = "";

    constructor(config: PropertyFilterConfig) {
        this.locationSearch = LocationSearchWithFallback();
        this.guests = GuestsStepper();
        this.dateFlexibility = DateFlexibility();
        this.advancedFilters = AdvancedFilters(config.filter);
        this.events = ref(new EventEmitter<PropertyFiltersEvents>()) as EventEmitter<PropertyFiltersEvents>;

        // Create debounced function
        this.debouncedEmitChange = debounce(() => {
            const currentFilters = this.getQueryParam();

            // Only emit if filters actually changed
            if (currentFilters !== this.lastAppliedFilters) {
                this.lastAppliedFilters = currentFilters;
                this.events.emit("apply");
            }
        }, 300);

        // Apply destination params when location is selected
        this.locationSearch.events.on("select", (option) => {
            if (option.params) {
                this.applyDestinationParams(option.params);
            }
        });
    }

    /**
     * Apply destination-specific params to current filters
     * User-selected filters take priority over destination defaults
     */
    private applyDestinationParams(destinationParams: string): void {
        const dateParams = this.dateFlexibility.dateRange.getQueryParam("checkIn", "checkOut");
        const flexibleParams = this.dateFlexibility.committedIsFlexible ? `flexible=true&nights=${this.dateFlexibility.committedNights || ""}` : "";
        const advancedParams = this.advancedFilters.getQueryParam();

        const currentFilters = [dateParams, flexibleParams, this.guests.getQueryParam(), advancedParams].filter(Boolean).join("&");

        const mergedParams = new URLSearchParams(mergeQueryParams(destinationParams, currentFilters));

        // Update guests from merged params
        const _adultsFromDest = mergedParams.get("adults");
        const _childrenFromDest = mergedParams.get("children");
        const _petsFromDest = mergedParams.get("pets");

        if (_adultsFromDest) {
            this.guests.adults.setValue(parseInt(_adultsFromDest, 10), true, true);
        }
        if (_childrenFromDest) {
            this.guests.children.setValue(parseInt(_childrenFromDest, 10), true, true);
        }
        if (_petsFromDest) {
            this.guests.pets.setValue(parseInt(_petsFromDest, 10), true, true);
        }

        const _flexibleFromDest = mergedParams.get("flexible");
        if (_flexibleFromDest === "true") {
            this.dateFlexibility.flexibilityRadio.setValue(true);
        }

        const housingTypeFromDest = mergedParams.get("housingType");
        if (housingTypeFromDest) {
            this.advancedFilters.setCheckboxesFromParam(
                housingTypeFromDest,
                this.advancedFilters.propertyTypes,
                (item) => [item.type.toLowerCase()],
                (value) => ({
                    type: value,
                    displayName: value,
                    count: 0,
                    checkbox: Checkbox({initialValue: true}),
                }),
            );
        }
    }

    [EVENTS](): void {
        // Listen to location changes
        this.locationSearch.events.on(["select", "clear"], () => {
            this.debouncedEmitChange();
        });

        // Listen to date flexibility changes
        this.dateFlexibility.events.on(["change", "clear"], () => {
            this.debouncedEmitChange();
        });

        // Listen to guests apply (on dropdown close or Update click)
        this.guests.events.on("apply", () => {
            this.debouncedEmitChange();
        });

        this.advancedFilters.events.on("apply", () => {
            this.debouncedEmitChange();
        });
    }

    // Check if any filter has a value
    get hasValue(): boolean {
        return this.locationSearch.hasValue || this.dateFlexibility.hasValue || this.guests.hasValue || this.advancedFilters.hasValue;
    }

    // Get query string for filtering properties
    getQueryParam(): string {
        return [
            this.locationSearch.getQueryParamSlug(),
            this.dateFlexibility.getQueryParam(),
            this.guests.getQueryParam(),
            this.advancedFilters.getQueryParam(),
        ]
            .filter(Boolean)
            .join("&");
    }

    // Set filters from query params
    setFromQueryParams(params: URLSearchParams): void {
        // Set location
        const location = params.get("location");

        if (location) {
            this.locationSearch.setValue(location);
        } else {
            this.locationSearch.clear(true);
        }

        // Set date flexibility
        this.dateFlexibility.setFromQueryParams(params);

        // Set guests
        this.guests.setFromQueryParams(params);

        // Set advanced filters
        this.advancedFilters.setFromQueryParams(params);

        // Update last applied filters to prevent duplicate apply on init
        this.lastAppliedFilters = this.getQueryParam();
    }

    // Reset all filters
    reset(silent = false): void {
        this.locationSearch.clear();
        this.dateFlexibility.clear();
        this.guests.reset();
        this.guests.adults.setValue(1);
        this.advancedFilters.clear();

        // Clear last applied filters
        this.lastAppliedFilters = "";

        if (!silent) {
            this.events.emit("reset");
        }
    }

    // Trigger update when advanced filters change
    onAdvancedFiltersUpdate(): void {
        this.debouncedEmitChange();
    }

    clearAdvance(): void {
        this.advancedFilters.clear();
        this.onAdvancedFiltersUpdate();
    }

    /**
     * Clear location filter when map bounds take priority (searchInBounds).
     * Resets lastAppliedFilters so re-selecting the same location still triggers a new search.
     */
    clearLocationForBoundsSearch(): void {
        this.locationSearch.clearWithOutLastSlug();
        this.lastAppliedFilters = this.getQueryParam();
    }

    dispose(): void {
        // Clear pending debounced calls
        this.debouncedEmitChange.clear();
    }
}

// Export type
export type PropertyFiltersInstance = PropertyFiltersClass;

export function PropertyFilters(config: PropertyFilterConfig): PropertyFiltersInstance {
    const instance = proxy(new PropertyFiltersClass(config));
    instance[EVENTS]();
    return instance;
}
